/**
 * Order within a Kanban column: the card moved into the column last stands
 * at the top. `statusChangedAt` is set by a database trigger on every status
 * change and on nothing else, so a note, a ticked checklist step or marking
 * a card leaves the column order alone.
 *
 * Equal timestamps (backfilled rows, a bulk move) fall back to the id; the
 * API order is not stable across refetches and would shuffle them otherwise.
 */
export function byStatusEntryDesc<T extends { id: string; statusChangedAt: string }>(
  a: T,
  b: T,
): number {
  const diff = new Date(b.statusChangedAt).getTime() - new Date(a.statusChangedAt).getTime();
  if (diff !== 0) return diff;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The reversed column order: the card waiting longest stands at the top, so
 * an inbox column can be worked off first-in, first-out. Ties keep the same
 * id order as the default, so flipping the switch only moves cards whose
 * timestamps differ.
 */
export function byStatusEntryAsc<T extends { id: string; statusChangedAt: string }>(
  a: T,
  b: T,
): number {
  const diff = new Date(a.statusChangedAt).getTime() - new Date(b.statusChangedAt).getTime();
  if (diff !== 0) return diff;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
